import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { fmt, fmtSigned } from '../utils/helpers';

const HEADERS = ['Date', 'JV No', 'Cheque No', 'Narration', 'Contra Accounts', 'Dr Amount', 'Cr Amount', 'Balance'];

const RelatedAccounts = ({ accounts }) => {
    if (!accounts?.length) return <span className="tw-text-gray-300">—</span>;
    if (accounts.length === 1) {
        const a = accounts[0];
        return (
            <span className="tw-text-xs tw-text-gray-600" title={`${a.accountCode || ''} ${a.accountName || ''}`.trim()}>
                {a.accountName || a.accountCode || '—'}
            </span>
        );
    }
    const tooltip = accounts.map(a => `${a.accountCode || ''} - ${a.accountName || ''}`.trim()).join(', ');
    return (
        <span className="tw-text-xs tw-text-blue-600 tw-underline tw-cursor-help" title={tooltip}>
            {accounts.length} accounts
        </span>
    );
};

const Amount = ({ value, kind }) => {
    if (!value) return <span className="tw-text-gray-300">—</span>;
    const colorClass = kind === 'dr' ? 'tw-text-red-600' : 'tw-text-green-600';
    return <span className={`tw-font-medium ${colorClass}`}>{fmt(value)}</span>;
};

const EntryRow = ({ entry, dense = false }) => (
    <tr className="tw-border-b tw-border-gray-50 hover:tw-bg-blue-50/20 tw-transition-colors">
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-text-gray-600 tw-whitespace-nowrap`}>
            {entry.date || '—'}
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-font-medium tw-text-blue-700 tw-whitespace-nowrap`}>
            {entry.journalNo || '—'}
            {entry.entryType === 'opening' && (
                <span className="tw-ml-1.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-blue-100 tw-text-blue-700 tw-text-[9px] tw-font-semibold">
                    Brought Forward
                </span>
            )}
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-text-gray-600 tw-whitespace-nowrap`}>
            {entry.chequeNo || '—'}
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-text-gray-700 tw-max-w-xs tw-truncate`} title={entry.narration}>
            {entry.narration || '—'}
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'}`}>
            <RelatedAccounts accounts={entry.relatedAccounts} />
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-text-right tw-whitespace-nowrap`}>
            <Amount value={entry.drAmount} kind="dr" />
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-text-right tw-whitespace-nowrap`}>
            <Amount value={entry.crAmount} kind="cr" />
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2'} tw-text-xs tw-font-semibold tw-text-right tw-text-gray-800 tw-whitespace-nowrap`}>
            {fmtSigned(entry.balance)}
        </td>
    </tr>
);

const LedgerTable = ({
    entries = [],
    openingEntries = [],
    openingBalance = 0,
    openingTotalDebit = 0,
    openingTotalCredit = 0,
    totalDebit = 0,
    totalCredit = 0,
    closingBalance = 0,
    reportInfo,
}) => {
    const [openingExpanded, setOpeningExpanded] = useState(false);
    const showOpening = openingEntries.length > 0 || openingBalance !== 0;
    const hasOpeningDetail = openingEntries.length > 0;

    // Auto-collapse the opening drill-down whenever a new report loads
    useEffect(() => { setOpeningExpanded(false); }, [reportInfo?.fromDate, reportInfo?.toDate, reportInfo?.accountCode]);

    const isEmpty = !showOpening && entries.length === 0;

    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
            <div className="tw-overflow-x-auto">
                <table className="tw-w-full">
                    <thead>
                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                            {HEADERS.map(h => (
                                <th
                                    key={h}
                                    className={`tw-px-3 tw-py-2.5 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-whitespace-nowrap ${h === 'Dr Amount' || h === 'Cr Amount' || h === 'Balance' ? 'tw-text-right' : 'tw-text-left'}`}
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {/* Opening Balance summary row + drill-down */}
                        {showOpening && (
                            <>
                                <tr
                                    className={`tw-border-b tw-border-gray-100 tw-bg-amber-50/60 ${hasOpeningDetail ? 'hover:tw-bg-amber-100/60 tw-cursor-pointer' : ''}`}
                                    onClick={() => hasOpeningDetail && setOpeningExpanded(s => !s)}
                                >
                                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                                        {reportInfo?.openingToDate || '—'}
                                    </td>
                                    <td colSpan={2} className="tw-px-3 tw-py-2 tw-text-xs tw-text-gray-400">—</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-amber-700">
                                        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                            {hasOpeningDetail && (
                                                <Icon
                                                    icon={openingExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                                                    className="tw-text-base tw-text-amber-700"
                                                />
                                            )}
                                            Opening Balance
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2" />
                                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-right tw-whitespace-nowrap">
                                        {openingTotalDebit ? <span className="tw-text-red-600">{fmt(openingTotalDebit)}</span> : <span className="tw-text-gray-300">—</span>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-text-right tw-whitespace-nowrap">
                                        {openingTotalCredit ? <span className="tw-text-green-600">{fmt(openingTotalCredit)}</span> : <span className="tw-text-gray-300">—</span>}
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-right tw-text-amber-700 tw-whitespace-nowrap">
                                        {fmtSigned(openingBalance)}
                                    </td>
                                </tr>

                                {hasOpeningDetail && openingExpanded && (
                                    <tr>
                                        <td colSpan={HEADERS.length} className="tw-p-0 tw-bg-amber-50/30">
                                            <div className="tw-px-3 tw-py-2 tw-border-y tw-border-amber-100">
                                                <p className="tw-text-[11px] tw-text-amber-800 tw-font-semibold tw-mb-1">
                                                    Opening balance breakdown
                                                    {reportInfo?.openingFromDate && reportInfo?.openingToDate && (
                                                        <span className="tw-font-normal tw-text-amber-700">
                                                            {' '}— {reportInfo.openingFromDate} to {reportInfo.openingToDate}
                                                        </span>
                                                    )}
                                                </p>
                                                <table className="tw-w-full">
                                                    <thead>
                                                        <tr className="tw-bg-white tw-border-b tw-border-amber-100">
                                                            {HEADERS.map(h => (
                                                                <th
                                                                    key={h}
                                                                    className={`tw-px-3 tw-py-2 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-whitespace-nowrap ${h === 'Dr Amount' || h === 'Cr Amount' || h === 'Balance' ? 'tw-text-right' : 'tw-text-left'}`}
                                                                >
                                                                    {h}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="tw-bg-white">
                                                        {openingEntries.map((entry, ei) => (
                                                            <EntryRow key={`oe-${ei}`} entry={entry} dense />
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}

                        {/* Body entries */}
                        {entries.map((entry, ei) => (
                            <EntryRow key={`e-${ei}`} entry={entry} />
                        ))}

                        {isEmpty && (
                            <tr>
                                <td colSpan={HEADERS.length} className="tw-text-center tw-py-16">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200" />
                                        <p className="tw-text-gray-400 tw-text-xs">No transactions found for selected filters</p>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* Totals / Closing row */}
                        {(entries.length > 0 || showOpening) && (
                            <tr className="tw-bg-gray-50 tw-border-t-2 tw-border-gray-200">
                                <td colSpan={5} className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-gray-700">Total</td>
                                <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-right tw-text-red-600 tw-whitespace-nowrap">{fmt(totalDebit)}</td>
                                <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-right tw-text-green-600 tw-whitespace-nowrap">{fmt(totalCredit)}</td>
                                <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-right tw-text-blue-700 tw-whitespace-nowrap">{fmtSigned(closingBalance)}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LedgerTable;
